import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../context/UserContext";
import {
  createTenantDocument,
  listTenantDocuments,
  updateTenantDocument,
} from "../../services/documentLibraryService";
import {
  DOCUMENT_LIBRARY_CATEGORIES,
  DOCUMENT_LIBRARY_CATEGORY_BY_KEY,
} from "../../constants/documentLibraryCategories";

const STATUS_OPTIONS = ["concept", "ter_controle", "goedgekeurd", "vervallen", "gearchiveerd"];
const FILE_TYPE_OPTIONS = ["", "pdf", "docx", "xlsx", "image", "other"];

const DEFAULT_FORM = {
  title: "",
  description: "",
  categoryKey: "photo_evidence",
  fileType: "",
  originalName: "",
  workorderId: "",
  projectId: "",
  assetId: "",
};

function getUserId(user) {
  return String(user?._id || user?.userId || user?.id || "").trim();
}

function getPermissionSet(user) {
  return new Set(
    (Array.isArray(user?.permissions) ? user.permissions : [])
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean)
  );
}

function getRoleSet(user) {
  const set = new Set();
  const directRoles = [user?.rol, user?.role];
  for (const value of directRoles) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized) set.add(normalized);
  }

  for (const role of Array.isArray(user?.roles) ? user.roles : []) {
    if (!role) continue;
    if (typeof role === "string") {
      const normalized = role.trim().toLowerCase();
      if (normalized) set.add(normalized);
      continue;
    }
    const name = String(role?.name || role?.naam || "").trim().toLowerCase();
    if (name) set.add(name);
  }

  return set;
}

function hasAnyPermission(user, keys = []) {
  const set = getPermissionSet(user);
  return keys.some((key) => set.has(String(key || "").trim().toLowerCase()));
}

function hasMonteurRole(user) {
  return getRoleSet(user).has("monteur");
}

function linkedLabel(item) {
  const linkedTo = item?.linkedTo || {};
  const parts = [];
  if (linkedTo.workorderId) parts.push("werkbon");
  if (linkedTo.projectId) parts.push("project");
  if (linkedTo.assetId) parts.push("installatie");
  return parts.length ? parts.join(", ") : "-";
}

function asTrimmed(value) {
  return String(value || "").trim();
}

export default function MonteurDocumentenPage() {
  const { user } = useUser();
  const userId = useMemo(() => getUserId(user), [user]);
  const monteurRole = useMemo(() => hasMonteurRole(user), [user]);

  const canViewOwn = useMemo(
    () => hasAnyPermission(user, ["tenant.documents.viewOwn", "tenant.documents.view"]) || monteurRole,
    [monteurRole, user]
  );
  const canCreateOwn = useMemo(
    () => hasAnyPermission(user, ["tenant.documents.createOwn", "tenant.documents.create"]) || monteurRole,
    [monteurRole, user]
  );
  const canUpdateOwnDraft = useMemo(
    () => hasAnyPermission(user, ["tenant.documents.updateOwnDraft", "tenant.documents.update"]) || monteurRole,
    [monteurRole, user]
  );
  const canSubmitForReview = useMemo(
    () => hasAnyPermission(user, ["tenant.documents.submitForReview", "tenant.documents.update"]) || monteurRole,
    [monteurRole, user]
  );

  const [filters, setFilters] = useState({
    category: "",
    status: "",
    search: "",
    workorderId: "",
    projectId: "",
    assetId: "",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editForm, setEditForm] = useState(null);

  const loadDocuments = async () => {
    if (!canViewOwn) {
      setLoading(false);
      setItems([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const query = { own: true };
      if (filters.category) query.category = filters.category;
      if (filters.status) query.status = filters.status;
      if (filters.search) query.search = filters.search;
      if (filters.workorderId) query.workorderId = asTrimmed(filters.workorderId);
      if (filters.projectId) query.projectId = asTrimmed(filters.projectId);
      if (filters.assetId) query.assetId = asTrimmed(filters.assetId);
      const data = await listTenantDocuments(query);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.message || "Mijn documenten laden mislukt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewOwn]);

  const submitCreate = async (nextStatus) => {
    if (!canCreateOwn) return;
    if (nextStatus === "ter_controle" && !canSubmitForReview) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        title: asTrimmed(form.title),
        description: asTrimmed(form.description),
        documentType: "general",
        fileType: asTrimmed(form.fileType) || "other",
        category: {
          key: form.categoryKey,
          label: DOCUMENT_LIBRARY_CATEGORY_BY_KEY[form.categoryKey]?.label || "Overig",
        },
        status: nextStatus,
        storage: {
          originalName: asTrimmed(form.originalName),
        },
        linkedTo: {
          workorderId: asTrimmed(form.workorderId),
          projectId: asTrimmed(form.projectId),
          assetId: asTrimmed(form.assetId),
        },
      };

      await createTenantDocument(payload);
      setMessage(
        nextStatus === "ter_controle"
          ? "Document ingediend ter controle."
          : "Document opgeslagen als concept."
      );
      setForm(DEFAULT_FORM);
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || "Document opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    if (!item?._id) return;
    setEditForm({
      id: item._id,
      title: item.title || "",
      description: item.description || "",
      categoryKey: item?.category?.key || "other",
      fileType: item.fileType || "",
      workorderId: item?.linkedTo?.workorderId || "",
      projectId: item?.linkedTo?.projectId || "",
      assetId: item?.linkedTo?.assetId || "",
    });
    setError("");
    setMessage("");
  };

  const saveEdit = async () => {
    if (!editForm?.id || !canUpdateOwnDraft) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateTenantDocument(editForm.id, {
        title: asTrimmed(editForm.title),
        description: asTrimmed(editForm.description),
        fileType: asTrimmed(editForm.fileType) || "other",
        category: {
          key: editForm.categoryKey,
          label: DOCUMENT_LIBRARY_CATEGORY_BY_KEY[editForm.categoryKey]?.label || "Overig",
        },
        linkedTo: {
          workorderId: asTrimmed(editForm.workorderId),
          projectId: asTrimmed(editForm.projectId),
          assetId: asTrimmed(editForm.assetId),
        },
      });
      setMessage("Conceptdocument bijgewerkt.");
      setEditForm(null);
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || "Concept bijwerken mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async (item) => {
    if (!item?._id || !canSubmitForReview) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateTenantDocument(item._id, { status: "ter_controle" });
      setMessage("Document ingediend ter controle.");
      if (editForm?.id === item._id) {
        setEditForm(null);
      }
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || "Indienen ter controle mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Mijn documenten</h2>
        <p className="mt-1 text-sm text-gray-600">
          Upload of registreer documenten die horen bij jouw werkbonnen, projecten of installaties.
        </p>
      </section>

      {!canViewOwn ? (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          Je hebt geen toegang tot Mijn documenten.
        </section>
      ) : null}

      {error ? (
        <section className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700">{error}</section>
      ) : null}
      {message ? (
        <section className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-700">{message}</section>
      ) : null}

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Filters</h3>
        <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            className="rounded border p-2 text-sm"
          >
            <option value="">Alle categorieen</option>
            {DOCUMENT_LIBRARY_CATEGORIES.map((category) => (
              <option key={category.key} value={category.key}>{category.label}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="rounded border p-2 text-sm"
          >
            <option value="">Alle statussen</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            className="rounded border p-2 text-sm"
            placeholder="Zoekterm"
          />
          <input
            value={filters.workorderId}
            onChange={(event) => setFilters((prev) => ({ ...prev, workorderId: event.target.value }))}
            className="rounded border p-2 text-sm"
            placeholder="Werkbon ID"
          />
          <input
            value={filters.projectId}
            onChange={(event) => setFilters((prev) => ({ ...prev, projectId: event.target.value }))}
            className="rounded border p-2 text-sm"
            placeholder="Project ID"
          />
          <input
            value={filters.assetId}
            onChange={(event) => setFilters((prev) => ({ ...prev, assetId: event.target.value }))}
            className="rounded border p-2 text-sm"
            placeholder="Installatie ID"
          />
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={loadDocuments}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Filter toepassen
          </button>
        </div>
      </section>

      {canCreateOwn ? (
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Document toevoegen</h3>
            <span className="text-xs text-gray-500">Uploadflow volgt in vervolgstap, metadata is nu actief.</span>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitCreate("concept");
            }}
            className="space-y-3"
          >
            <div className="grid gap-3 md:grid-cols-2">
              <input
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Titel"
                className="rounded border p-2 text-sm"
                required
                disabled={saving}
              />
              <select
                value={form.categoryKey}
                onChange={(event) => setForm((prev) => ({ ...prev, categoryKey: event.target.value }))}
                className="rounded border p-2 text-sm"
                required
                disabled={saving}
              >
                {DOCUMENT_LIBRARY_CATEGORIES.map((category) => (
                  <option key={category.key} value={category.key}>{category.label}</option>
                ))}
              </select>
              <textarea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Omschrijving (optioneel)"
                className="rounded border p-2 text-sm"
                rows={3}
                disabled={saving}
              />
              <select
                value={form.fileType}
                onChange={(event) => setForm((prev) => ({ ...prev, fileType: event.target.value }))}
                className="rounded border p-2 text-sm"
                disabled={saving}
              >
                {FILE_TYPE_OPTIONS.map((type) => (
                  <option key={type || "none"} value={type}>{type || "Bestandstype (optioneel)"}</option>
                ))}
              </select>
              <input
                value={form.originalName}
                onChange={(event) => setForm((prev) => ({ ...prev, originalName: event.target.value }))}
                placeholder="Bestandsnaam (voorbereiding upload)"
                className="rounded border p-2 text-sm"
                disabled={saving}
              />
              <input
                value={form.workorderId}
                onChange={(event) => setForm((prev) => ({ ...prev, workorderId: event.target.value }))}
                placeholder="Werkbon ID (optioneel)"
                className="rounded border p-2 text-sm"
                disabled={saving}
              />
              <input
                value={form.projectId}
                onChange={(event) => setForm((prev) => ({ ...prev, projectId: event.target.value }))}
                placeholder="Project ID (optioneel)"
                className="rounded border p-2 text-sm"
                disabled={saving}
              />
              <input
                value={form.assetId}
                onChange={(event) => setForm((prev) => ({ ...prev, assetId: event.target.value }))}
                placeholder="Installatie ID (optioneel)"
                className="rounded border p-2 text-sm"
                disabled={saving}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Opslaan als concept
              </button>
              <button
                type="button"
                onClick={() => submitCreate("ter_controle")}
                disabled={saving || !canSubmitForReview}
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-60"
              >
                Indienen ter controle
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Je hebt alleen leesrechten voor Mijn documenten.
        </section>
      )}

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Documenten</h3>
          <span className="text-xs text-gray-500">{items.length} items</span>
        </div>

        {loading ? <p className="text-sm text-gray-500">Laden...</p> : null}
        {!loading && items.length === 0 ? (
          <p className="text-sm text-gray-500">Geen documenten gevonden.</p>
        ) : null}

        <div className="space-y-3">
          {items.map((item) => {
            const isOwn = String(item?.createdBy || "") === userId;
            const canEditDraft = isOwn && canUpdateOwnDraft && item?.status === "concept";
            const canSubmitDraft = isOwn && canSubmitForReview && item?.status === "concept";
            const isEditing = editForm?.id === item._id;

            return (
              <article key={item._id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">{item.title || "(zonder titel)"}</h4>
                    <p className="mt-1 text-xs text-slate-600">
                      {item?.category?.label || "Overig"} · {item?.status || "-"} · gekoppeld aan: {linkedLabel(item)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Aangemaakt op: {item?.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {canEditDraft ? (
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                      >
                        Bewerken
                      </button>
                    ) : null}
                    {canSubmitDraft ? (
                      <button
                        type="button"
                        onClick={() => submitForReview(item)}
                        className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800"
                      >
                        Indienen ter controle
                      </button>
                    ) : null}
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    <input
                      value={editForm.title}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
                      className="rounded border p-2 text-sm"
                      placeholder="Titel"
                    />
                    <select
                      value={editForm.categoryKey}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, categoryKey: event.target.value }))}
                      className="rounded border p-2 text-sm"
                    >
                      {DOCUMENT_LIBRARY_CATEGORIES.map((category) => (
                        <option key={category.key} value={category.key}>{category.label}</option>
                      ))}
                    </select>
                    <textarea
                      value={editForm.description}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, description: event.target.value }))}
                      className="rounded border p-2 text-sm"
                      rows={3}
                      placeholder="Omschrijving"
                    />
                    <select
                      value={editForm.fileType}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, fileType: event.target.value }))}
                      className="rounded border p-2 text-sm"
                    >
                      {FILE_TYPE_OPTIONS.map((type) => (
                        <option key={type || "none"} value={type}>{type || "Bestandstype (optioneel)"}</option>
                      ))}
                    </select>
                    <input
                      value={editForm.workorderId}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, workorderId: event.target.value }))}
                      className="rounded border p-2 text-sm"
                      placeholder="Werkbon ID"
                    />
                    <input
                      value={editForm.projectId}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, projectId: event.target.value }))}
                      className="rounded border p-2 text-sm"
                      placeholder="Project ID"
                    />
                    <input
                      value={editForm.assetId}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, assetId: event.target.value }))}
                      className="rounded border p-2 text-sm"
                      placeholder="Installatie ID"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={saving}
                        className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                      >
                        Opslaan
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm(null)}
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        Annuleren
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
